import { Link } from "react-router-dom";
import {
  Phone,
  MessageSquare,
  ArrowLeft,
  ShieldCheck,
  Wrench,
  Check,
  Zap,
  Award,
  Crown,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { JdBio } from "@/components/site/JdBio";
import { InspectionModal } from "@/components/site/InspectionModal";
import { SHINGLE_LINES, PHONE, PHONE_TEL } from "@/lib/constants";

const TIER_ICON = {
  Good: Award,
  Better: Sparkles,
  Best: Crown,
};

export default function Shingles() {
  return (
    <>
      <Header />

      {/* HERO */}
      <section
        className="relative overflow-hidden border-b-4 border-red-600"
        style={{
          background:
            "radial-gradient(circle at 18% 30%, rgba(30,58,138,0.55) 0%, transparent 55%), radial-gradient(circle at 82% 70%, rgba(220,38,38,0.35) 0%, transparent 55%), linear-gradient(180deg, #020617 0%, #0b1220 60%, #020617 100%)",
        }}
      >
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.07] pointer-events-none mix-blend-screen"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-16 md:pb-20">
          <Link
            to="/"
            data-testid="back-home"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-sky-300 mb-6 hover:text-white transition-colors"
          >
            <ArrowLeft size={14} /> Back home
          </Link>
          <div className="inline-flex items-center gap-2 bg-red-600/15 border border-red-600/60 px-3 py-1 mb-6">
            <Zap size={13} className="text-red-500 fill-red-500" />
            <span className="text-[11px] uppercase tracking-[0.3em] text-white font-bold">
              GAF Premium Lineup
            </span>
          </div>
          <h1 className="font-impact uppercase text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-tight max-w-5xl">
            Pick The Roof That{" "}
            <span
              style={{
                background: "linear-gradient(180deg, #ffffff 0%, #93c5fd 60%, #1e40af 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
              }}
            >
              Protects
            </span>{" "}
            Your Home.
          </h1>
          <p className="text-slate-200 text-lg md:text-xl max-w-3xl mt-6 leading-relaxed font-medium">
            After your claim is approved, you'll pick from <span className="text-white font-bold">three premium GAF Timberline lines</span>.
            Every shingle is backed by a <span className="text-sky-300 font-bold">Limited Lifetime Warranty</span> and installed by certified pros.
          </p>
          {/* Trust strip */}
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl">
            {[
              "Limited Lifetime Warranty",
              "Pay Only Your Deductible",
              "GAF Certified Installers",
              "Most Roofs In 1 Day",
            ].map((t) => (
              <div
                key={t}
                className="flex items-center gap-2 bg-slate-900/70 border border-white/10 px-3 py-2"
              >
                <Check size={14} className="text-red-500 shrink-0" />
                <span className="text-[11px] uppercase tracking-wider text-white font-bold">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GOOD / BETTER / BEST CARDS */}
      <section data-testid="shingle-gallery" className="py-16 md:py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-12 md:mb-16">
            <div className="text-[11px] uppercase tracking-[0.4em] text-sky-300 font-bold mb-3">
              Good · Better · Best
            </div>
            <h2 className="font-impact uppercase text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none">
              Three Lines.{" "}
              <span className="text-red-500">One Built For You.</span>
            </h2>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 lg:gap-6 xl:gap-8">
            {SHINGLE_LINES.map((s, i) => {
              const TierIcon = TIER_ICON[s.tier] || Award;
              const isPopular = s.upgrade?.popular;
              const isPremium = s.tier === "Best";
              return (
                <article
                  key={s.name}
                  data-testid={`shingle-line-${i}`}
                  className={`relative flex flex-col bg-slate-900/60 border-2 ${
                    isPopular
                      ? "border-sky-400 shadow-[0_0_40px_rgba(125,211,252,0.35)]"
                      : isPremium
                      ? "border-red-600 shadow-[0_0_40px_rgba(220,38,38,0.4)]"
                      : "border-white/15"
                  } overflow-hidden transition-transform hover:-translate-y-2 hover:shadow-[0_24px_60px_rgba(0,0,0,0.55)]`}
                >
                  {/* Popular / Premium ribbon */}
                  {(isPopular || isPremium) && (
                    <div
                      className={`absolute top-4 right-0 z-20 text-white font-impact uppercase text-xs tracking-[0.25em] px-4 py-1.5 ${
                        isPopular ? "bg-sky-500" : "bg-red-600"
                      }`}
                      style={{ clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 8% 100%)" }}
                    >
                      {isPopular ? "Most Popular" : "Top of the Line"}
                    </div>
                  )}

                  {/* House + shingle photo */}
                  <div className="relative aspect-square overflow-hidden bg-slate-950">
                    <img
                      src={s.image}
                      alt={`${s.shortName} GAF roof example`}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                    />
                    {/* dark bottom gradient for the giant name */}
                    <div
                      aria-hidden
                      className="absolute inset-x-0 bottom-0 h-2/5 pointer-events-none"
                      style={{
                        background:
                          "linear-gradient(180deg, rgba(2,6,23,0) 0%, rgba(2,6,23,0.55) 50%, rgba(2,6,23,0.95) 100%)",
                      }}
                    />
                    {/* HUGE shingle name overlay */}
                    <div className="absolute inset-x-0 bottom-0 px-5 pb-4">
                      <div className="flex items-center gap-2 mb-1.5">
                        <TierIcon size={14} className="text-sky-300" />
                        <span className="text-[10px] uppercase tracking-[0.3em] text-sky-300 font-bold">
                          {s.tier} · {s.tag}
                        </span>
                      </div>
                      <h3
                        data-testid={`tier-${i}`}
                        className="font-impact uppercase text-white tracking-tight leading-[0.85] text-5xl sm:text-6xl"
                        style={{
                          textShadow:
                            "0 4px 18px rgba(0,0,0,0.85), 0 0 22px rgba(220,38,38,0.35)",
                        }}
                      >
                        {s.shortName}
                      </h3>
                    </div>
                  </div>

                  {/* Card body */}
                  <div className="flex flex-col flex-1 p-6 md:p-7">
                    <div className="text-xs text-slate-400 font-bold uppercase tracking-wider mb-2">
                      {s.name}
                    </div>
                    <p className="text-slate-300 leading-relaxed text-sm mb-5">
                      {s.blurb}
                    </p>

                    {/* Pricing card */}
                    {s.upgrade && (
                      <div
                        data-testid={`pricing-${i}`}
                        className={`mb-5 border-2 px-4 py-3 ${
                          s.upgrade.free
                            ? "border-emerald-500/70 bg-emerald-500/10"
                            : isPopular
                            ? "border-sky-400 bg-sky-500/10"
                            : "border-red-600 bg-red-600/10"
                        }`}
                      >
                        <div className="flex items-baseline justify-between gap-2">
                          <span
                            className={`text-[10px] uppercase tracking-[0.25em] font-bold ${
                              s.upgrade.free
                                ? "text-emerald-400"
                                : isPopular
                                ? "text-sky-300"
                                : "text-red-400"
                            }`}
                          >
                            {s.upgrade.label}
                          </span>
                          <span className="font-impact text-white text-2xl uppercase tracking-tight">
                            {s.upgrade.price}
                          </span>
                        </div>
                        {s.upgrade.note && (
                          <div className="text-[11px] text-slate-400 mt-1.5">
                            {s.upgrade.note}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Perks list */}
                    <ul className="space-y-2 mb-6">
                      {s.perks.map((p) => (
                        <li
                          key={p}
                          className="flex items-start gap-2 text-sm text-slate-200"
                        >
                          <Check
                            size={14}
                            className={`shrink-0 mt-1 ${
                              isPopular
                                ? "text-sky-400"
                                : isPremium
                                ? "text-red-500"
                                : "text-emerald-400"
                            }`}
                          />
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>

                    {/* Warranty pair */}
                    <div className="mt-auto pt-4 border-t border-white/10">
                      <div className="text-[10px] uppercase tracking-[0.25em] text-red-500 font-bold mb-3">
                        Warranty Coverage
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-slate-950/70 border border-white/10 p-3">
                          <Wrench size={14} className="text-sky-300 mb-1" />
                          <div className="font-impact text-white text-base uppercase tracking-tight leading-tight">
                            {s.warranty.labor}
                          </div>
                          <div className="text-[10px] text-slate-400 mt-0.5 uppercase tracking-wider">
                            Workmanship
                          </div>
                        </div>
                        <div className="bg-slate-950/70 border border-white/10 p-3">
                          <ShieldCheck size={14} className="text-red-500 mb-1" />
                          <div className="font-impact text-white text-base uppercase tracking-tight leading-tight">
                            {s.warranty.material}
                          </div>
                          <div className="text-[10px] text-slate-400 mt-0.5 uppercase tracking-wider">
                            GAF Backed
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Color chips */}
                    <div className="mt-5">
                      <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-2">
                        Popular Colors
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {s.colors.map((c) => (
                          <span
                            key={c}
                            className="text-[10px] uppercase tracking-wider font-bold text-slate-200 border border-white/15 px-2 py-1 bg-slate-950/50"
                          >
                            {c}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* COMPARISON STRIP */}
      <section className="py-12 bg-gradient-to-r from-slate-950 via-blue-950/40 to-slate-950 border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-4 gap-6 text-center">
          {[
            { n: "100%", l: "Roofs Backed By Lifetime Warranty" },
            { n: "1 Day", l: "Most Replacements Completed" },
            { n: "$0", l: "Out-of-Pocket With Approved Claim" },
            { n: "27", l: "Georgia Counties Served" },
          ].map((s) => (
            <div key={s.l}>
              <div className="font-impact text-white text-5xl md:text-6xl tracking-tight leading-none">
                <span className="text-red-500">{s.n}</span>
              </div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-slate-300 mt-2 font-bold">
                {s.l}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section
        className="relative overflow-hidden border-y-4 border-red-600"
        style={{
          background:
            "linear-gradient(90deg, #7f1d1d 0%, #b91c1c 18%, #0c1a3d 50%, #b91c1c 82%, #7f1d1d 100%)",
        }}
      >
        <div className="relative max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-20 text-center">
          <div className="text-[11px] uppercase tracking-[0.4em] text-sky-300 font-bold mb-3">
            Step 1 · Free Inspection
          </div>
          <h2 className="font-impact uppercase text-white text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95] drop-shadow-[0_4px_24px_rgba(0,0,0,0.55)]">
            See If Your Roof <br />
            <span className="text-sky-300">Qualifies For Free.</span>
          </h2>
          <p className="text-slate-100 text-lg sm:text-xl mt-5 max-w-2xl mx-auto font-semibold">
            JD inspects, documents the damage, and walks you through every shingle option in person — at no cost.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-10">
            <InspectionModal>
              <button
                type="button"
                data-testid="shingles-cta-book"
                className="bg-white text-slate-950 font-impact uppercase tracking-wider hover:bg-slate-100 px-8 py-5 inline-flex items-center justify-center gap-3 text-xl transition-all hover:-translate-y-0.5 border-2 border-white"
              >
                <ShieldCheck size={20} className="text-red-600" />
                Book Free Inspection
                <ArrowRight size={16} />
              </button>
            </InspectionModal>
            <a
              data-testid="shingles-call"
              href={`tel:${PHONE_TEL}`}
              className="bg-slate-950 text-white font-impact uppercase tracking-wider hover:bg-slate-800 px-8 py-5 inline-flex items-center justify-center gap-3 text-xl transition-all border-2 border-white"
            >
              <Phone size={20} className="text-red-500" /> {PHONE}
            </a>
            <a
              data-testid="shingles-text"
              href={`sms:${PHONE_TEL}`}
              className="bg-red-600 text-white font-impact uppercase tracking-wider hover:bg-red-500 px-8 py-5 inline-flex items-center justify-center gap-3 text-xl transition-all border-2 border-white"
            >
              <MessageSquare size={20} /> Text JD
            </a>
          </div>
        </div>
      </section>

      <JdBio />

      <Footer />
    </>
  );
}
